import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import FlavoriaPage from "../components/FlavoriaPage";

const GRILL_QUEUE_API = "https://flavoria-grill-queue.tt.utu.fi/queue";
const FULL_QUEUE_URL = "https://skj-ddc9a2e4.eu.ngrok.io/files/index.html#orders";
const POLL_INTERVAL = 5000;
const STALE_THRESHOLD_MS = 60000;
const GREEN_BG = "#1b5e20";
const RED_BG = "#b71c1c";
const GRAY_BG = "#616161";

interface QueueItem {
  ID: string;
  Estimate?: string;
}

interface QueueStatus {
  ready_count: number;
  inpipeline_count: number;
  max_estimate_seconds: number;
  max_estimate_readable: string;
  fetched_at: string;
  current_time?: string;
  ready_ids: QueueItem[];
  inpipeline_ids: QueueItem[];
}

interface DisplayItem extends QueueItem {
  isReady: boolean;
  isMonitored: boolean;
}

const GrillQueue: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [monitoredIds, setMonitoredIds] = useState<Set<string>>(new Set());
  const [flashingIds, setFlashingIds] = useState<Set<string>>(new Set());
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0);
  const clockOffsetRef = useRef(0);
  const lastSuccessfulFetch = useRef<number>(0);
  const prevReadyIds = useRef<Set<string>>(new Set());
  const intervalRef = useRef<number | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  const stale = lastSuccessfulFetch.current !== 0 && Date.now() - lastSuccessfulFetch.current > STALE_THRESHOLD_MS;

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(GRILL_QUEUE_API);
      if (response.status === 204) {
        setStatus(null);
        setFetchError(false);
        lastSuccessfulFetch.current = Date.now();
        return;
      }
      if (!response.ok) throw new Error("Failed to fetch");
      const data: QueueStatus = await response.json();
      const serverNow = data.current_time ? new Date(data.current_time).getTime() : Date.now();
      clockOffsetRef.current = serverNow - Date.now();
      const serverFetchedAt = new Date(data.fetched_at).getTime();
      const localFetchedAt = serverFetchedAt - clockOffsetRef.current;
      setStatus(data);
      setFetchError(false);
      setFetchedAt(localFetchedAt);
      setSecondsSinceUpdate(0);
      lastSuccessfulFetch.current = Date.now();

      const readyIdSet = new Set(data.ready_ids.map((item) => item.ID));
      const monitoredArr = Array.from(monitoredIds);
      const monitoredReadyNow = new Set(
        monitoredArr.filter((id) => readyIdSet.has(id))
      );
      const monitoredReadyBefore = new Set(
        monitoredArr.filter((id) => prevReadyIds.current.has(id))
      );

      if (monitoredIds.size > 0) {
        const newlyReady = Array.from(monitoredReadyNow).filter(
          (id) => !monitoredReadyBefore.has(id)
        );
        if (newlyReady.length > 0) {
          try { navigator.vibrate?.(2000); } catch { /* vibrate not available */ }

          setFlashingIds((prev) => {
            const next = new Set(prev);
            for (const id of newlyReady) {
              next.add(id);
            }
            return next;
          });
        }
      }

      prevReadyIds.current = readyIdSet;
    } catch {
      setFetchError(true);
    }
  }, [monitoredIds]);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = window.setInterval(fetchStatus, POLL_INTERVAL);
    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
      setFlashingIds(new Set());
    };
  }, [fetchStatus]);

  useEffect(() => {
    if (fetchedAt === null) return;
    const tick = window.setInterval(() => {
      setSecondsSinceUpdate(Math.floor((Date.now() - fetchedAt) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [fetchedAt]);

  useEffect(() => {
    const acquire = async () => {
      if (!("wakeLock" in navigator)) return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {
          // Wake lock not supported
        }
    };
    const release = async () => {
      if (wakeLockRef.current) {
        try {
          await wakeLockRef.current.release();
        } catch {
          // Wake lock release failed
        }
        wakeLockRef.current = null;
      }
    };

    if (monitoredIds.size > 0) {
      acquire();
    } else {
      release();
    }

    return () => { release(); };
  }, [monitoredIds]);

  const toggleMonitoredId = (id: string) => {
    const next = new Set(monitoredIds);
    if (next.has(id)) {
      next.delete(id);
      setFlashingIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } else {
      next.add(id);
      if (status?.ready_ids.some((item) => item.ID === id)) {
        setFlashingIds((prev) => { const n = new Set(prev); n.add(id); return n; });
      }
    }
    setMonitoredIds(next);
  };

  const hasData = !fetchError && !stale && status !== null;

  const buildDisplayItems = (): DisplayItem[] => {
    if (!status) return [];
    const items: DisplayItem[] = [];
    const inpipelineReversed = [...status.inpipeline_ids].reverse();
    const readyReversed = [...status.ready_ids].reverse();
    for (const item of inpipelineReversed) {
      if (monitoredIds.has(item.ID)) {
        items.push({ ...item, isReady: false, isMonitored: true });
      }
    }
    for (const item of inpipelineReversed) {
      if (!monitoredIds.has(item.ID)) {
        items.push({ ...item, isReady: false, isMonitored: false });
      }
    }
    for (const item of readyReversed) {
      if (monitoredIds.has(item.ID)) {
        items.push({ ...item, isReady: true, isMonitored: true });
      }
    }
    for (const item of readyReversed) {
      if (!monitoredIds.has(item.ID)) {
        items.push({ ...item, isReady: true, isMonitored: false });
      }
    }
    return items;
  };

  const allIds = buildDisplayItems();

  const getItemStyle = (item: DisplayItem): React.CSSProperties => {
    let bg: string;
    if (item.isMonitored) {
      bg = item.isReady ? GREEN_BG : RED_BG;
    } else {
      bg = item.isReady ? GREEN_BG : GRAY_BG;
    }
    return {
      padding: "12px 8px",
      backgroundColor: bg,
      color: "#fff",
      borderRadius: "8px",
      textAlign: "center",
      fontWeight: item.isMonitored ? "bold" : "normal",
      cursor: !item.isReady ? "pointer" : "default",
      opacity: item.isMonitored || item.isReady ? 1 : 0.8,
      transition: "all 0.5s",
      fontSize: "1.5em",
      border: item.isMonitored ? "2px solid rgba(255,255,255,0.6)" : "none",
      boxShadow: item.isMonitored ? "0 0 10px rgba(0,0,0,0.25)" : "none",
      animation: flashingIds.has(item.ID) ? "flash-ready 1.5s ease-in-out infinite" : "none",
    };
  };

  return (
    <FlavoriaPage className="grill-queue" pageID="ACTIVE_PAGE_GRILL_QUEUE">
      <style>{`
        @keyframes flash-ready {
          0%, 100% { background-color: ${GREEN_BG}; }
          95% { background-color: #a5d6a7; }
        }
      `}</style>
      <div style={{ padding: "20px" }}>
        {!hasData && fetchError && (
          <p>{t("Grill info temporarily not available")}</p>
        )}
        {!hasData && !fetchError && status === null && (
          <p>{t("No grill information right now")}</p>
        )}

        {hasData && status && (
          <>
            <IonCard>
              <IonCardContent>
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "4px 12px" }}>
                  <span>{t("GRILL_IN_PIPELINE")}</span>
                  <span>{status.inpipeline_count}</span>
                  <span>{t("GRILL_ESTIMATE")}</span>
                  <span>{status.max_estimate_readable}</span>
                  <span>{t("GRILL_READY")}</span>
                  <span>{status.ready_count}</span>
                </div>
                <p style={{ fontSize: "0.85em", color: "var(--ion-color-medium)", marginTop: "8px" }}>
                  {t("GRILL_UPDATED", { seconds: secondsSinceUpdate })}
                </p>
              </IonCardContent>
            </IonCard>

            <IonCard>
              <IonCardHeader>
                <IonCardTitle>{t("GRILL_QUEUE_GRID")}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {monitoredIds.size > 0 && status && (() => {
                  const monitoredItems = [
                    ...status.inpipeline_ids.filter(i => monitoredIds.has(i.ID)),
                    ...status.ready_ids.filter(i => monitoredIds.has(i.ID)),
                  ];
                  return (
                    <div style={{ marginBottom: "12px", fontSize: "0.85em" }}>
                      <span style={{ color: "var(--ion-color-medium)" }}>
                        {t("GRILL_MONITORING", { count: monitoredIds.size })}
                      </span>
                      <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", gap: "2px" }}>
                        {monitoredItems.map(item => (
                          <span key={item.ID}>
                            <strong>{item.ID}</strong> — {item.Estimate ?? "—"}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                  gap: "8px",
                }}>
                  {allIds.map((item) => (
                    <div
                      key={item.ID}
                      onClick={() => {
                        if (item.isMonitored) {
                          toggleMonitoredId(item.ID);
                        } else if (!item.isReady) {
                          toggleMonitoredId(item.ID);
                        }
                      }}
                      style={getItemStyle(item)}
                    >
                      {item.ID}
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: "16px", marginTop: "16px", flexWrap: "wrap", fontSize: "0.85em", alignItems: "center" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: GRAY_BG }} />
                    {t("GRILL_LEGEND_IN_PIPELINE")}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: GREEN_BG }} />
                    {t("GRILL_LEGEND_READY")}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: RED_BG }} />
                    {t("GRILL_LEGEND_MONITORED")}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: GREEN_BG }} />
                    <span style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: "#a5d6a7" }} />
                    {t("GRILL_LEGEND_FLASH")}
                  </span>
                </div>
                <p style={{ fontSize: "0.85em", marginTop: "12px", color: "var(--ion-color-medium)", lineHeight: 1.4 }}>
                  {t("GRILL_CLICK_INSTRUCTION")}
                </p>
              </IonCardContent>
            </IonCard>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px" }}>
          <IonButton fill="solid" href={FULL_QUEUE_URL} target="_blank" rel="noopener noreferrer">
            {t("View full queue")}
          </IonButton>
        </div>

        <p style={{ fontSize: "0.8em", color: "var(--ion-color-medium)", textAlign: "center", padding: "0 16px 16px", lineHeight: 1.4 }}>
          {t("GRILL_EXPERIMENTAL")}
        </p>
      </div>
    </FlavoriaPage>
  );
};

export default GrillQueue;
